import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react"
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query"
import {
  getBaseUrl,
  getDevBypassHeader,
  getLogoutUrl,
  getMiddlewareRoot,
} from "@/utils/helpers/configs"
import { applySubmissionEmailOverrideToFetchArgs } from "@/utils/debug/submissionEmailOverride"

const baseUrl = getBaseUrl()

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  credentials: "include",
  prepareHeaders: (headers: Headers) => {
    const devBypass = getDevBypassHeader()
    if (devBypass) {
      headers.set("X-Dev-Bypass", devBypass.toString())
    }
    return headers
  },
})

// wrap with retry (but no automatic retries, we’ll do refresh manually)
const baseQueryWithRetry = retry(rawBaseQuery, { maxRetries: 0 })

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requestArgs = applySubmissionEmailOverrideToFetchArgs(args)
  let result = await baseQueryWithRetry(requestArgs, api, extraOptions)

  if (result.error && "status" in result.error && result.error.status === 401) {
    try {
      // attempt refresh using direct fetch call to avoid circular dependency
      const apiRoot = getMiddlewareRoot()
      const refreshResult = await baseQueryWithRetry(
        {
          url: `${apiRoot}/jwt/refresh`,
          method: "POST",
        },
        api,
        extraOptions
      )

      if (!refreshResult.error) {
        // retry original request after successful refresh
        result = await baseQueryWithRetry(requestArgs, api, extraOptions)
      } else {
        // refresh failed → force logout
        window.location.href = getLogoutUrl()
      }
    } catch {
      window.location.href = getLogoutUrl()
    }
  }

  return result
}
